import { JSX, useEffect, useState } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore framer-motion is in the parent dir
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, EditIcon, TrashIcon, XIcon } from 'lucide-react';

import { Button } from 'components/Button';
import { Checkbox } from 'components/Checkbox';
import { Label } from 'components/Label';
import { Select } from 'components/Select';
import { Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography';
import { useToast } from 'hooks/useToaster';
import { Commodity, NormalizedLoad } from 'types/Load';
import { Maybe } from 'types/UtilityTypes';
import { cn } from 'utils/shadcn';

import { LoadBuildingTextInput } from '../LoadBuildingTextInput';
import {
  MGB_DEFAULT_WEIGHT_UNIT,
  MGB_PIECE_UNIT_OPTIONS,
  dimensionUnitsList,
  freightClassList,
  weightUnitsList,
} from '../constants';
import {
  ensureValidMGBCommodityLegIndices,
  isMGBDropStopType,
  isMGBPickupStopType,
  mgbCommodityStopFieldIndex,
  mgbStopLabel,
} from '../mgbStops';
import {
  isEmptyMGBCommodity,
  isPositiveMGBDimension,
  mgPieceUnitLabel,
} from '../utils';

type CommodityUnitFieldName =
  | `commodities.${number}.totalPieces.unit`
  | `commodities.${number}.weightUnit`
  | `commodities.${number}.dimensionUnit`;

type CommodityUnitOption = { value: string; label: string };

type MGBCommodityStopSelectProps = {
  fieldValue: unknown;
  stopsLength: number;
  placeholder: string;
  options: ReadonlyArray<{ index: number; label: string }>;
  onChange: (index: number) => void;
};

function MGBCommodityStopSelect({
  fieldValue,
  stopsLength,
  placeholder,
  options,
  onChange,
}: MGBCommodityStopSelectProps) {
  const selectedIndex = mgbCommodityStopFieldIndex(fieldValue, stopsLength);
  const selectValue =
    selectedIndex != null &&
    options.some((option) => option.index === selectedIndex)
      ? String(selectedIndex)
      : undefined;

  return (
    <Select
      showSearch
      className='h-8 mt-1 text-neutral-600'
      placeholder={placeholder}
      optionFilterProp='children'
      value={selectValue}
      onChange={(value) => {
        const next = Number(value);
        if (Number.isFinite(next)) {
          onChange(next);
        }
      }}
      options={options.map((option) => ({
        value: String(option.index),
        label: option.label,
      }))}
    />
  );
}

function CommodityUnitSelect({
  name,
  label = 'Units',
  options,
  control,
  requiredMessage,
}: {
  name: CommodityUnitFieldName;
  label?: string;
  options: ReadonlyArray<string | CommodityUnitOption>;
  control: UseFormReturn<NormalizedLoad>['control'];
  requiredMessage?: string;
}) {
  const selectOptions = options.map((unit) =>
    typeof unit === 'string' ? { value: unit, label: unit } : unit
  );

  return (
    <>
      <Label
        name={name}
        required={Boolean(requiredMessage)}
        className='text-xs'
      >
        {label}
      </Label>
      <Controller
        name={name}
        control={control}
        rules={
          requiredMessage
            ? {
                validate: (value) =>
                  String(value ?? '').trim() ? true : requiredMessage,
              }
            : undefined
        }
        render={({ field, fieldState }) => (
          <>
            <Select
              showSearch
              className='h-8 mt-1 text-neutral-600'
              placeholder='Choose'
              optionFilterProp='children'
              onChange={field.onChange}
              value={
                field.value && String(field.value).trim()
                  ? String(field.value)
                  : undefined
              }
              options={selectOptions}
            />
            {fieldState.error?.message ? (
              <Typography variant='body-xs' className='text-error-500 mt-1'>
                {fieldState.error.message}
              </Typography>
            ) : null}
          </>
        )}
      />
    </>
  );
}

export interface CommodityItemInputCardProps {
  formMethods: UseFormReturn<NormalizedLoad>;
  item: Commodity;
  index: number;
  onRemove: (index: number) => void;
  onSave?: () => void;
  isDeletable?: boolean;
}

const ANIMATIONS = {
  card: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
  },
  toggle: {
    initial: { opacity: 0, height: 0 },
    animate: { opacity: 1, height: 'auto' },
    exit: { opacity: 0, height: 0 },
    transition: { duration: 0.15 },
  },
  actions: {
    initial: { opacity: 0, scale: 0.8 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.8 },
    transition: { duration: 0.1 },
  },
};

export function CommodityItemInputCard({
  formMethods,
  item,
  index,
  onRemove,
  onSave,
  isDeletable = true,
}: CommodityItemInputCardProps): JSX.Element {
  const { toast } = useToast();
  const { trigger, watch, setValue, control, clearErrors, getValues } =
    formMethods;
  const [isEditing, setIsEditing] = useState(false);
  const [originalValues, setOriginalValues] = useState<Maybe<Commodity>>(null);
  const watchedItem = watch(`commodities.${index}`);
  const allCommodities = watch('commodities') ?? [];
  const watchedStops = watch('stops') ?? [];
  const routeStops = getValues('stops') ?? watchedStops;
  const showStopAssignment = routeStops.length > 2;
  const pickupStopOptions = routeStops.flatMap((stop, stopIdx) =>
    isMGBPickupStopType(stop.stopType)
      ? [{ index: stopIdx, label: mgbStopLabel(routeStops, stopIdx) }]
      : []
  );
  const dropStopOptions = routeStops.flatMap((stop, stopIdx) =>
    isMGBDropStopType(stop.stopType)
      ? [{ index: stopIdx, label: mgbStopLabel(routeStops, stopIdx) }]
      : []
  );
  const pickupStopNumber = watch(`commodities.${index}.pickupStopNumber`);
  const dropStopNumber = watch(`commodities.${index}.dropStopNumber`);

  // Nested path only — never setValue('commodities', [...]) here.
  // Replacing the whole field-array parent wipes sibling nested pick/drop values.
  const syncCommodityStopField = (
    field: 'pickupStopNumber' | 'dropStopNumber',
    value: number
  ) => {
    setValue(`commodities.${index}.${field}`, value, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    clearErrors(`commodities.${index}.pickupStopNumber`);
    clearErrors(`commodities.${index}.dropStopNumber`);
    clearErrors('commodities');
  };

  const isEmpty = isEmptyMGBCommodity(watchedItem);

  const requiresDimensionUnit =
    isPositiveMGBDimension(watchedItem?.length) ||
    isPositiveMGBDimension(watchedItem?.width) ||
    isPositiveMGBDimension(watchedItem?.height);

  useEffect(() => {
    if (isEmpty && !isEditing) {
      setOriginalValues({ ...watchedItem });
      setIsEditing(true);
    }
  }, [isEmpty, isEditing, watchedItem]);

  useEffect(() => {
    if (!isEditing) {
      return;
    }
    const dimensionUnitPath = `commodities.${index}.dimensionUnit` as const;
    if (requiresDimensionUnit) {
      void trigger(dimensionUnitPath);
      return;
    }
    clearErrors(dimensionUnitPath);
  }, [requiresDimensionUnit, isEditing, index, trigger, clearErrors]);

  const handlers = {
    edit: () => {
      setOriginalValues({ ...watchedItem });
      setIsEditing(true);
    },
    save: async () => {
      if (isEmpty) {
        const hasOtherMeaningful = allCommodities.some(
          (c, i) => i !== index && !isEmptyMGBCommodity(c)
        );
        if (!hasOtherMeaningful) {
          toast({
            description:
              'At least one commodity with description, pieces and weight is required.',
            variant: 'destructive',
          });
          return;
        }
        handlers.remove();
        return;
      }

      const isValid = await trigger(`commodities.${index}`);
      if (!isValid) {
        return;
      }

      // Normalize stale pick/drop after route edits before closing the row.
      if (routeStops.length > 2) {
        const leg = ensureValidMGBCommodityLegIndices(
          routeStops,
          getValues(`commodities.${index}.pickupStopNumber`),
          getValues(`commodities.${index}.dropStopNumber`)
        );
        setValue(`commodities.${index}.pickupStopNumber`, leg.pickup, {
          shouldDirty: true,
        });
        setValue(`commodities.${index}.dropStopNumber`, leg.drop, {
          shouldDirty: true,
        });
        clearErrors('commodities');
      }

      if (onSave) {
        onSave();
      }
      setIsEditing(false);
      setOriginalValues(null);
    },
    discard: () => {
      if (originalValues) {
        setValue(`commodities.${index}`, originalValues);
        if (onSave) {
          onSave();
        }
      }
      setIsEditing(false);
      setOriginalValues(null);
    },
    remove: () => onRemove(index),
  };

  const ActionButton = ({
    onClick,
    className,
    icon: Icon,
    'aria-label': ariaLabel,
    disabled = false,
    title,
  }: {
    onClick: () => void;
    className: string;
    icon: React.ComponentType<{ className?: string }>;
    'aria-label': string;
    disabled?: boolean;
    title?: string;
  }) => {
    const tooltip = title ?? ariaLabel;
    const button = (
      <Button
        onClick={onClick}
        className={cn(
          'p-1 rounded-full transition-colors duration-150',
          className
        )}
        variant='ghost'
        size='xs'
        buttonNamePosthog={null}
        aria-label={ariaLabel}
        title={tooltip}
        type='button'
        disabled={disabled}
      >
        <Icon className='h-4 w-4' />
      </Button>
    );

    // Disabled buttons use pointer-events-none; wrap so the tooltip still shows.
    if (disabled) {
      return <span title={tooltip}>{button}</span>;
    }

    return button;
  };

  const removeCommodityButton = (
    <ActionButton
      onClick={handlers.remove}
      className={
        isDeletable ? 'hover:bg-error-400 text-error-600' : 'text-neutral-400'
      }
      icon={TrashIcon}
      aria-label='Remove commodity'
      disabled={!isDeletable}
      title={
        isDeletable
          ? 'Remove commodity'
          : 'At least one commodity item is required'
      }
    />
  );

  const renderActions = () => (
    <AnimatePresence mode='wait'>
      {isEditing ? (
        <motion.div
          key='editing-actions'
          {...ANIMATIONS.actions}
          className='flex gap-1.5'
        >
          <ActionButton
            onClick={handlers.save}
            className='hover:bg-info-200 text-info-600'
            icon={CheckIcon}
            aria-label='Save changes'
          />
          {!isEmpty && (
            <ActionButton
              onClick={handlers.discard}
              className='hover:bg-error-200 text-error-600'
              icon={XIcon}
              aria-label='Discard changes'
            />
          )}
          {isEmpty && removeCommodityButton}
        </motion.div>
      ) : (
        <motion.div
          key='view-actions'
          {...ANIMATIONS.actions}
          className='flex gap-1.5'
        >
          <ActionButton
            onClick={handlers.edit}
            className='hover:bg-info-300 text-info-600'
            icon={EditIcon}
            aria-label='Edit commodity'
          />
          {removeCommodityButton}
        </motion.div>
      )}
    </AnimatePresence>
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      const target = e.target as HTMLElement;

      if (
        target.closest('.ant-select') ||
        target.closest('.ant-select-dropdown') ||
        target.classList.contains('ant-select-search__field') ||
        target.closest('.ant-select-selection-search')
      ) {
        return;
      }

      if (isEditing) {
        e.preventDefault();
        e.stopPropagation();
        handlers.save();
      }
    }
  };

  const renderEditingForm = () => (
    <motion.div
      key='editing-form'
      {...ANIMATIONS.toggle}
      className='overflow-hidden'
    >
      <Flex justify='between' align='center' className='mb-2'>
        <Flex align='center' gap='md'>
          <span className='text-xs font-semibold text-neutral-600'>
            Commodity {index + 1}
          </span>
        </Flex>

        <div onClick={(e) => e.stopPropagation()}>{renderActions()}</div>
      </Flex>

      <Grid cols='4' gap='lg' className='mx-0 w-full'>
        <div className='col-span-4'>
          <LoadBuildingTextInput
            name={`commodities.${index}.description`}
            label='Description'
            labelClassName='text-xs'
            placeholder='Product description'
            options={{ required: 'Description is required' }}
          />
        </div>

        {showStopAssignment ? (
          <>
            <div className='col-span-2'>
              <Label
                name={`commodities.${index}.pickupStopNumber`}
                className='text-xs'
                required
              >
                Picked At
              </Label>
              <MGBCommodityStopSelect
                fieldValue={pickupStopNumber}
                stopsLength={routeStops.length}
                placeholder='Select pickup'
                options={pickupStopOptions}
                onChange={(value) =>
                  syncCommodityStopField('pickupStopNumber', value)
                }
              />
            </div>
            <div className='col-span-2'>
              <Label
                name={`commodities.${index}.dropStopNumber`}
                className='text-xs'
                required
              >
                Delivered To
              </Label>
              <MGBCommodityStopSelect
                fieldValue={dropStopNumber}
                stopsLength={routeStops.length}
                placeholder='Select drop'
                options={dropStopOptions}
                onChange={(value) =>
                  syncCommodityStopField('dropStopNumber', value)
                }
              />
            </div>
          </>
        ) : null}

        <div className='col-span-4'>
          <LoadBuildingTextInput
            name={`commodities.${index}.referenceNumber`}
            label='Item ID'
            labelClassName='text-xs'
            placeholder='Optional — defaults to ITEM if blank'
          />
        </div>

        <Grid cols='2' gap='sm' className='mx-0 w-full col-span-4'>
          <div className='col-span-1'>
            <LoadBuildingTextInput
              name={`commodities.${index}.totalPieces.val`}
              label='Pieces'
              labelClassName='text-xs'
              inputType='number'
              step={1}
              options={{
                required: 'Pieces is required',
                min: { value: 1, message: 'Pieces must be greater than 0' },
              }}
            />
          </div>
          <div className='col-span-1'>
            <CommodityUnitSelect
              name={`commodities.${index}.totalPieces.unit`}
              options={MGB_PIECE_UNIT_OPTIONS}
              control={control}
              requiredMessage='Required'
            />
          </div>
        </Grid>

        <Grid cols='2' gap='sm' className='mx-0 w-full col-span-4'>
          <div className='col-span-1'>
            <LoadBuildingTextInput
              name={`commodities.${index}.weightTotal`}
              label='Weight'
              labelClassName='text-xs'
              inputType='number'
              step={0.01}
              options={{
                required: 'Weight is required',
                min: { value: 0.01, message: 'Weight must be greater than 0' },
              }}
            />
          </div>
          <div className='col-span-1'>
            <CommodityUnitSelect
              name={`commodities.${index}.weightUnit`}
              options={weightUnitsList}
              control={control}
              requiredMessage='Required'
            />
          </div>
        </Grid>

        <div className='col-span-2'>
          <Label name={`commodities.${index}.freightClass`} className='text-xs'>
            Freight Class
          </Label>
          <Controller
            name={`commodities.${index}.freightClass`}
            control={control}
            render={({ field }) => (
              <Select
                className='h-8'
                placeholder='Choose'
                value={field.value || undefined}
                options={freightClassList.map((value) => ({
                  value,
                  label: value,
                }))}
                onChange={field.onChange}
              />
            )}
          />
        </div>
        <div className='col-span-2'>
          <LoadBuildingTextInput
            name={`commodities.${index}.nmfc`}
            label='NMFC Code'
            labelClassName='text-xs'
            placeholder='e.g. 114110-03'
          />
        </div>

        <Grid cols='4' gap='sm' className='mx-0 w-full col-span-4'>
          <div className='col-span-1'>
            <LoadBuildingTextInput
              name={`commodities.${index}.length`}
              label='Length'
              labelClassName='text-xs'
              inputType='number'
              step={0.01}
            />
          </div>
          <div className='col-span-1'>
            <LoadBuildingTextInput
              name={`commodities.${index}.width`}
              label='Width'
              labelClassName='text-xs'
              inputType='number'
              step={0.01}
            />
          </div>
          <div className='col-span-1'>
            <LoadBuildingTextInput
              name={`commodities.${index}.height`}
              label='Height'
              labelClassName='text-xs'
              inputType='number'
              step={0.01}
            />
          </div>
          <div className='col-span-1'>
            <CommodityUnitSelect
              name={`commodities.${index}.dimensionUnit`}
              label='UOM'
              options={dimensionUnitsList}
              control={control}
              requiredMessage={
                requiresDimensionUnit
                  ? 'UOM is required when L,W,or H is set'
                  : undefined
              }
            />
          </div>
        </Grid>

        <div className='col-span-4'>
          <Controller
            name={`commodities.${index}.hazardousMaterial`}
            control={control}
            render={({ field: hazmatField }) => (
              <Checkbox
                id={`commodities-${index}-hazardousMaterial`}
                label='Hazardous Materials'
                checked={Boolean(hazmatField.value)}
                onCheckedChange={(checked) =>
                  hazmatField.onChange(checked === true)
                }
              />
            )}
          />
        </div>
      </Grid>
    </motion.div>
  );

  const renderViewMode = () => {
    const displayItem = watchedItem ?? item;
    const description = displayItem.description?.trim();
    const referenceNumber = displayItem.referenceNumber?.trim();
    const displayTitle =
      description ||
      (referenceNumber
        ? `Item ID: ${referenceNumber}`
        : `Commodity ${index + 1}`);

    const weightUnit =
      displayItem.weightUnit?.trim() || MGB_DEFAULT_WEIGHT_UNIT;

    return (
      <motion.div
        key='view-mode'
        {...ANIMATIONS.toggle}
        className='overflow-hidden cursor-pointer'
        onClick={handlers.edit}
        onKeyDown={(e: React.KeyboardEvent) =>
          e.key === 'Enter' && handlers.edit()
        }
        role='button'
        tabIndex={0}
        aria-label='Click to edit commodity'
      >
        <Flex justify='between' align='center' className='min-w-0'>
          <Flex align='center' gap='md' className='flex-1 min-w-0 mr-2'>
            <Typography
              variant='caption'
              className='text-neutral-600 font-medium truncate'
              title={displayTitle}
              aria-label={displayTitle}
            >
              {displayTitle}
            </Typography>
          </Flex>

          <Flex onClick={(e) => e.stopPropagation()} className='flex-shrink-0'>
            {renderActions()}
          </Flex>
        </Flex>

        <Flex align='center' gap='sm' className='text-sm text-neutral-900'>
          <Typography variant='body-xs' className='text-neutral-500'>
            Pieces:
          </Typography>
          <Typography variant='body-xs' className='text-neutral-600'>
            {displayItem.totalPieces?.val || 0}{' '}
            {mgPieceUnitLabel(displayItem.totalPieces?.unit) || 'PALLET'}
          </Typography>
          <Typography variant='body-xs' className='text-neutral-500'>
            •
          </Typography>
          <Typography
            variant='body-xs'
            className='font-semibold text-brand-main'
          >
            {displayItem.weightTotal || 0} {weightUnit}
          </Typography>
          {displayItem.hazardousMaterial ? (
            <>
              <Typography variant='body-xs' className='text-neutral-500'>
                •
              </Typography>
              <Typography variant='body-xs' className='text-neutral-600'>
                Hazmat
              </Typography>
            </>
          ) : null}
        </Flex>
        {referenceNumber && description ? (
          <Typography variant='body-xs' className='text-neutral-500 mt-1'>
            Item ID: {referenceNumber}
          </Typography>
        ) : null}
        {displayItem.nmfc ? (
          <Typography variant='body-xs' className='text-neutral-500 mt-1'>
            NMFC: {displayItem.nmfc}
          </Typography>
        ) : null}
      </motion.div>
    );
  };

  return (
    <motion.div
      layout
      transition={{ type: 'spring', duration: 0.2, bounce: 0 }}
      {...ANIMATIONS.card}
      className={cn(
        'w-full overflow-hidden rounded-[4px] border bg-linear-to-br shadow-sm relative transition-all duration-200 p-2.5',
        isEmpty
          ? 'border-neutral-400 bg-linear-to-br from-neutral-50 to-neutral-100/50'
          : 'border-neutral-500 bg-linear-to-br from-neutral-50 to-info-50/50'
      )}
      onKeyDown={handleKeyDown}
    >
      <AnimatePresence mode='wait'>
        {isEditing ? renderEditingForm() : renderViewMode()}
      </AnimatePresence>
    </motion.div>
  );
}
